import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import WelcomeOverlay from './components/WelcomeOverlay.tsx';
import EventCardSection from './components/EventCardSection.tsx';
import MainContent from './components/MainContent.tsx';
import QuickActions from './components/QuickActions.tsx';
import './App.css';
import { motion } from 'framer-motion';
import { COLORS } from './constants/colors.ts';

const App: React.FC = () => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [eventCardDisplayed, setEventCardDisplayed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const rsvpSectionRef = useRef<HTMLElement>(null);

  // Invitation opening handler
  const handleOpenInvitation = () => {
    setOverlayOpen(true);
    // Start showing content immediately, not after delay
    setShowContent(true);
  };

  const handleEventCardDisplayed = useCallback(() => {
    setEventCardDisplayed(true);
  }, []);

  // Autoplay music when content shows
  useEffect(() => {
    if (showContent && audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          // Autoplay was prevented, user will need to click play
          console.log('Autoplay prevented:', error);
        });
    }
  }, [showContent]);

  // Music controls
  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Scroll to RSVP section
  const scrollToRSVP = () => {
    rsvpSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="min-h-screen overflow-auto" style={{ backgroundColor: COLORS.primary }}>
      {/* Background Music */}
      <audio 
        ref={audioRef} 
        loop 
        src="/audio/wedding-song.mp3"
      />

      <AnimatePresence>
        {!overlayOpen && (
          <WelcomeOverlay onOpenInvitation={handleOpenInvitation} />
        )}
      </AnimatePresence>

      <div className="w-full max-w-[430px] mx-auto relative">
        {/* Event Card Section - Shows after overlay closes */}
        <EventCardSection showContent={showContent} onDisplayComplete={handleEventCardDisplayed} />

        {/* Content - shows only after event card animation completes */}
        {eventCardDisplayed && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="relative z-20"
            style={{ backgroundColor: COLORS.primary }}
          >
            <MainContent rsvpSectionRef={rsvpSectionRef} />
          </motion.div>
        )}

        {/* Quick Actions - Shows after content is visible */}
        {showContent && (
          <QuickActions 
            isPlaying={isPlaying}
            toggleMusic={toggleMusic}
            scrollToRSVP={scrollToRSVP}
          />
        )}
      </div>
    </div>
  );
};

export default App; 